import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'

import { useLogin, useMe, usePasskeyLogin } from '../../hooks/queries'
import { usePreparedPasskeyOptions } from '../../hooks/passkeyOptions'
import { autofillAvailable, isAbort, loginWithPasskey, passkeyAvailable, type LoginOptions } from '../../lib/passkey'
import { useTexte } from '../../texte'

export function LoginPage() {
  const texte = useTexte()
  const t = texte.anmeldung
  const me = useMe()
  const login = useLogin()
  const passkey = usePasskeyLogin()
  const suggestion = usePasskeyLogin()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passkeyCancelled, setPasskeyCancelled] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  // The challenge is fetched as the page opens, so that the button below can
  // call the browser inside the tap (`hooks/passkeyOptions.ts`).
  const prepared = usePreparedPasskeyOptions<LoginOptions>('/api/auth/passkeys/login/options', passkeyAvailable())

  // Wer einem Einladungslink gefolgt ist, soll nach dem Anmelden dort landen
  // und nicht auf der Startseite.
  const from = (location.state as { from?: string } | null)?.from ?? '/'

  /*
   * **The passkey in the address field** (#21). If the browser offers it, a
   * silent request runs from the moment the page opens; iOS then shows the
   * passkey above the keyboard as soon as the field has focus. It hangs on
   * an `AbortController`: the button below and leaving the page abort it --
   * the browser rejects two open requests at once.
   */
  useEffect(() => {
    if (!passkeyAvailable()) return
    let alive = true
    const controller = new AbortController()
    abortRef.current = controller
    void autofillAvailable().then((ok) => {
      if (!ok || !alive) return
      const assertion = prepared
        .promise()
        .then((options) => loginWithPasskey(options, controller.signal, 'conditional'))
      assertion.then(() => prepared.spend(), () => {})
      suggestion.mutate({ assertion }, { onSuccess: () => navigate(from) })
    })
    return () => {
      alive = false
      controller.abort()
      abortRef.current = null
    }
    // Once per page: the mutation itself does not change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (me.data) return <Navigate to={from} replace />

  const passkeyError = passkey.isError && !isAbort(passkey.error) ? passkey.error : null
  const suggestionError = suggestion.isError && !isAbort(suggestion.error) ? suggestion.error : null

  return (
    <div className="centered">
      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault()
          login.mutate({ email, password }, { onSuccess: () => navigate(from) })
        }}
      >
        <h1>{t.anmelden}</h1>
        <p className="card__lead">{texte.allgemein.name}</p>

        {login.isError && <p className="notice" role="alert">{login.error.message}</p>}
        {(passkeyError ?? suggestionError) && (
          <p className="notice" role="alert">{(passkeyError ?? suggestionError)?.message}</p>
        )}
        {passkeyCancelled && (
          <p className="notice notice--info" role="status">{t.passkeyAbgebrochen}</p>
        )}

        <label className="field">
          <span className="field__label">{t.email}</span>
          <input
            className="field__input"
            type="email"
            autoComplete="username webauthn"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">{t.passwort}</span>
          <input
            className="field__input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        <button className="button" type="submit" disabled={login.isPending}>
          {login.isPending ? texte.allgemein.laeuft : t.anmelden}
        </button>

        {passkeyAvailable() && (
          <>
            <p className="anmeldung__oder">{t.oder}</p>
            <button
              className="button button--outline"
              type="button"
              data-passkey="anmelden"
              disabled={passkey.isPending}
              onClick={() => {
                setPasskeyCancelled(false)
                // The silent request has to go before the visible one starts.
                abortRef.current?.abort()
                // **The browser is called here, in the tap, not after a round
                // trip.** Safari counts the gesture only until the handler
                // returns; the challenge is therefore already on hand. Only
                // when it is not (stale, or never arrived) does the slow way
                // remain.
                const options = prepared.ready()
                const assertion = options !== null
                  ? loginWithPasskey(options)
                  : prepared.promise().then((fetched) => loginWithPasskey(fetched))
                assertion.then(() => prepared.spend(), () => {})
                passkey.mutate(
                  { assertion },
                  {
                    onSuccess: () => navigate(from),
                    onError: (error) => setPasskeyCancelled(isAbort(error)),
                  },
                )
              }}
            >
              {passkey.isPending ? texte.allgemein.laeuft : t.mitPasskey}
            </button>
          </>
        )}

        <p style={{ marginTop: 14, textAlign: 'center', fontSize: 14 }}>
          <Link to="/forgot">{t.passwortVergessen}</Link>
        </p>

        <p style={{ marginTop: 4, textAlign: 'center', fontSize: 14 }}>
          {t.keinKontoVor}
          <Link to="/register">{t.registrierung}</Link>
        </p>
      </form>
    </div>
  )
}
